/**
 * The sidebar's structure, and the rules for what is open and highlighted.
 *
 * Kept out of DefaultLayout so it can be tested without booting the shell: the
 * "is this branch active" question has more cases than it looks like, and each
 * one is a row that either lights up correctly or quietly does not.
 *
 * Two sections mirroring the mock ("Workspace" / "Administration"). Empty
 * permission = always visible; otherwise gated on the user's permission list.
 * A child's `key` is a full path; a parent's is a route key.
 */
export const SECTIONS = [
  {
    heading: 'Workspace',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: 'dashboard', permission: '' },
      { key: 'orders', label: 'Orders', icon: 'orders', permission: 'VIEW_ORDERS' },
      { key: 'customers', label: 'Customers', icon: 'customers', permission: 'VIEW_CUSTOMERS' },
      { key: 'stock', label: 'Ready Stock', icon: 'stock', permission: 'VIEW_STOCK' },
      // The raw-fabric subsystem is four screens around one set of records, so
      // it takes one row in the nav and expands when you are inside it.
      {
        key: 'fabricStore',
        label: 'Fabric',
        icon: 'fabric',
        permission: 'VIEW_FABRIC_STOCK',
        children: [
          { key: '/fabricStore', label: 'Store' },
          { key: '/fabricReceipts', label: 'Deliveries' },
          { key: '/fabricSales', label: 'Sales' },
          { key: '/fabricPayments', label: 'Payments' },
        ],
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { key: 'users', label: 'Users', icon: 'users', permission: 'VIEW_USERS' },
      { key: 'settings', label: 'Settings', icon: 'settings', permission: 'VIEW_SETTINGS' },
    ],
  },
];

export const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * What one nav row should do, given where the user is.
 *
 * @param item              a SECTIONS entry
 * @param currentKey        first path segment, e.g. "fabricSales"
 * @param activePath        full pathname, e.g. "/fabricSales"
 * @param collapsed         icon-only sidebar
 * @param dashboardChildren Dashboard's children depend on the viewer's role, so
 *                          they are passed in rather than declared above
 */
export function navState(item, { currentKey, activePath, collapsed, dashboardChildren = null }) {
  const children = item.key === 'dashboard' ? dashboardChildren : item.children || null;
  const hasChildren = !!(children && children.length);

  // A group is active while any of its screens is — otherwise the whole branch
  // reads as unvisited from the page you are standing on.
  const active = item.key === currentKey
    || (children || []).some((c) => c.key === activePath);

  return {
    children,
    hasChildren,
    active,
    // Dashboard's children are always listed, as they were. A four-screen group
    // only expands once you are inside it, which is the point of grouping it.
    showChildren: !collapsed && hasChildren && (item.key === 'dashboard' || active),
  };
}

/**
 * The breadcrumb title for a route key.
 *
 * Children are titled under the group they sit in — "Fabric · Sales" rather
 * than a bare "Sales", which says nothing about which part of the system you
 * are in, or a raw "FabricSales" from the fallback.
 */
export function pageTitleFor(currentKey) {
  const items = SECTIONS.flatMap((s) => s.items);

  // Children first, deliberately. A group's landing page shares its key with
  // the group itself — "fabricStore" is both the Fabric row and its Store
  // child — and the child is the more specific answer, so it has to win the
  // lookup. Parent-first would title that one page "Fabric" while its three
  // siblings read "Fabric · Sales" and so on.
  const titles = [
    ...items.flatMap((item) => (item.children || []).map((child) => ({
      key: child.key.replace(/^\//, ''),
      label: `${item.label} · ${child.label}`,
    }))),
    ...items.map((item) => ({ key: item.key, label: item.label })),
  ];

  return titles.find((t) => t.key === currentKey)?.label || capitalize(currentKey) || 'Dashboard';
}
